import React from 'react';
import { GetStaticProps } from 'next';
import * as Sentry from '@sentry/nextjs';

import styles from './styles.module.scss';

import { withLayoutAndSession } from '@utils/hocs';
import { PageWithProvidersAndLayout } from '@utils/types/nextApp';
import MetaHead from '@components/_shared/metaHead';
import FeeStructureHero from '@components/landing/feeStructure/hero';
import FeeStructureContact from '@components/landing/feeStructure/contact';
import RovePlus from '@components/landing/feeStructure/rovePlus';
import {
  FeeStructureLandingType,
  LandingSuccessStoriesType,
} from '@utils/types/landings';
import { getFeeStructureLanding } from '@api/landings/getFeeStructureLanding';
import FAQs from '@components/landing/_shared/faqs';
import SuccessStories from '@components/landing/_shared/successStories';
import ImageBackground from '@components/_shared/imageBackground';

type Props = {
  feeStructureLanding: FeeStructureLandingType;
  successStories: LandingSuccessStoriesType;
};

const FeeStructure: PageWithProvidersAndLayout<Props> = (props) => {
  const {
    feeStructureLanding: {
      title,
      subtitle,
      cta,
      plans,
      rovePlus,
      contact,
      faq,
    },
    successStories,
  } = props;

  return (
    <>
      <MetaHead title={title} description={subtitle} />

      <div className={styles.page}>
        <FeeStructureHero
          title={title}
          subtitle={subtitle}
          cta={cta}
          plans={plans}
        />

        <hr className={styles.divider} />

        <SuccessStories {...successStories} />

        <ImageBackground
          containerStyle={styles.rovePlusContainer}
          backgroundImageStyle={`
            url('/images/fee-structure-rove-plus-bg.png')
          `}
        >
          <RovePlus {...rovePlus} />
        </ImageBackground>

        <FeeStructureContact {...contact} />

        <hr className={styles.divider} />

        <div className={styles.faq__container}>
          <div className={styles.faq__wrapper}>
            <h2 className={styles.faq__title}>{faq.title}</h2>
            <FAQs
              faqs={
                faq.faqItems?.map((f) => ({
                  question: f.question,
                  answerRaw: f.answer,
                })) || []
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps<any> = async () => {
  const props = await getFeeStructureLanding();

  if (!props) {
    Sentry.captureException(
      `Fee Structure Landing: getStaticProps: CMS returned no data for landing`
    );

    return {
      notFound: true,
      revalidate: 10,
    };
  }

  return {
    props,
    revalidate: 30,
  };
};

export default withLayoutAndSession(FeeStructure, {
  headerProps: { theme: 'dark' },
  footerProps: { theme: 'dark' },
});
